import type { MediaDevicePreferences } from "../../services/mediaDevicePreferences";
import type { SessionView } from "../../services/publicSession";
import { isInvitationGone, isSessionGone, publicErrorKey, type PublicFlowAction } from "./publicFlow";

// The guest's Ready request (POST …/ready, idempotent server-side). Triggered by the automatic
// readiness continuation or by the explicit retry after a failure — never repeated on its own:
//  - one request in flight at a time (StrictMode / double triggers / a retry click collapse);
//  - a failure shows an error with the existing explicit retry; nothing is resent automatically;
//  - a superseded/expired guest session (401) is re-issued from the stored link ONCE, followed by
//    exactly one Ready with the new session; a second 401 stops with an error (no loop);
//  - a closed invitation (410/404) shows the server's current state; if that state cannot be
//    fetched, an explicit recoverable error (with the retry) is shown instead of a silent wait;
//  - when the re-issue itself found the link closed, the page is already on the closed screen
//    and no further error is added.
// The server keeps its own authorization, readiness and one-time-invitation rules; this only
// decides when the client asks.

export interface GuestReadyDeps {
  token(): string | null;
  markReady(token: string): Promise<SessionView>;
  getSession(token: string): Promise<SessionView>;
  /**
   * Re-redeem the stored invitation link: the new session token; "closed" when the link is no
   * longer valid (the page already shows the closed state); null when it could not be reached.
   */
  reissue(): Promise<string | "closed" | null>;
  dispatch(action: PublicFlowAction): void;
}

export interface GuestReady {
  send(): Promise<void>;
  readonly inFlight: boolean;
}

/**
 * The guest readiness stage's onReady (automatic or manual after Leave): record the selected
 * devices + on/off state (they carry into the call) and send Ready (deduplicated by `ready`).
 */
export function guestDevicesReady(
  prefs: MediaDevicePreferences,
  dispatch: (action: PublicFlowAction) => void,
  ready: GuestReady
): Promise<void> {
  dispatch({ type: "devicesReady", prefs });
  return ready.send();
}

export function createGuestReady(deps: GuestReadyDeps): GuestReady {
  let inFlight = false;

  const attempt = async (token: string, mayReissue: boolean): Promise<void> => {
    try {
      deps.dispatch({ type: "view", view: await deps.markReady(token) });
    } catch (e) {
      if (isSessionGone(e)) {
        const fresh = mayReissue ? await deps.reissue() : null;
        if (fresh === "closed") return; // closed screen already shown
        if (fresh) return attempt(fresh, false);
        deps.dispatch({ type: "error", error: "unavailable" });
      } else if (isInvitationGone(e)) {
        const view = await deps.getSession(token).catch(() => null);
        if (view) deps.dispatch({ type: "view", view });
        else deps.dispatch({ type: "error", error: "unavailable" }); // recoverable: the retry stays visible
      } else {
        deps.dispatch({ type: "error", error: publicErrorKey(e) });
      }
    }
  };

  return {
    get inFlight() {
      return inFlight;
    },
    async send() {
      if (inFlight) return;
      inFlight = true;
      deps.dispatch({ type: "error", error: null });
      try {
        const token = deps.token();
        if (token) await attempt(token, true);
        else deps.dispatch({ type: "error", error: "unavailable" }); // keeps the explicit retry visible
      } finally {
        inFlight = false;
      }
    }
  };
}
